
import React, { useEffect, useRef } from 'react';
import { Users, CheckSquare, TrendingUp } from 'lucide-react';

const solutions = [
  {
    icon: <Users className="w-6 h-6" />,
    title: "Non-Affiliated Influencers",
    bullets: [
      "Influencers compensated through our platform, not your brand",
      "Authentic content without direct brand bias"
    ]
  },
  {
    icon: <CheckSquare className="w-6 h-6" />,
    title: "Third-Party Validated Data",
    bullets: [
      "All glucose data verified by independent testing protocols",
      "Results you and consumers can trust"
    ]
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Scalable Testing Pool",
    bullets: [
      "Access to thousands of qualified testers",
      "Case-specific matching for relevant results"
    ]
  }
];

const SolutionSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animate-on-scroll');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 150); // stagger the animations
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-neutral-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Independent Verification That Scales
          </h2>
          <p className="text-neutral-700 text-lg">
            Our platform delivers authentic testimonials backed by real glucose data that consumers trust.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-6 md:p-8 border border-neutral-200 shadow-soft animate-on-scroll flex flex-col"
            >
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <div className="text-primary">
                  {solution.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">{solution.title}</h3>
              <ul className="space-y-3 mt-2">
                {solution.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start">
                    <div className="mr-3 mt-1 text-green-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                    <span className="text-neutral-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
